/**
 * Document AI Processing Panel — Patient360 documents tab integration.
 *
 * Purpose:
 *  - Show all uploaded documents for the patient with their AI extraction status
 *  - Trigger / re-run OCR + classification per document
 *  - Review extracted structured fields, override the AI tag, edit values
 *  - Approve high-confidence extractions in bulk-friendly UX
 *
 * Roles (FE display only, real enforcement on backend):
 *  Care Coordinator, Care Coordinator Manager, PV, Admin
 */
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AppCard,
  AppCardContent,
  AppCardHeader,
  AppCardTitle,
} from '@/modules/shared/components/ui/AppCard';
import { Button } from '@/modules/shared/components/ui/button';
import { AppFormSkeleton, AppInlineLoader } from '@/modules/shared/components/ui/AppLoading';
import { Badge } from '@/modules/shared/components/ui/badge';
import { Input } from '@/modules/shared/components/ui/input';
import { Label } from '@/modules/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/modules/shared/components/ui/select';
import { useToast } from '@/modules/shared/components/ui/toast';
import {
  Sparkles,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Pencil,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  Link2,
  FileSearch,
} from 'lucide-react';
import { documentAiService } from '../../services/documentAiService';
import type {
  DocAiDocumentType,
  DocAiExtractionListItem,
} from '../../types/documentAi';
import {
  DOC_AI_TYPE_LABELS,
  DOC_AI_BAND_COLORS,
  DOC_AI_STATUS_COLORS,
} from '../../types/documentAi';

interface Props {
  programId: string;
  patientId: string;
}

const DOC_TYPES: DocAiDocumentType[] = [
  'INVOICE',
  'AE_REPORT',
  'PRESCRIPTION',
  'CONSENT_FORM',
  'TREATMENT_SUMMARY',
  'LAB_REPORT',
  'UNKNOWN',
];

export function DocumentAiPanel({ programId, patientId }: Props) {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const [expandedDocId, setExpandedDocId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['document-ai-list', programId, patientId],
    queryFn: () =>
      documentAiService.listExtractions(programId, { patientId, pageSize: 100 }),
    enabled: !!programId && !!patientId,
  });

  const items = listQuery.data?.items ?? [];

  // KPI roll-up
  const stats = useMemo(() => {
    const total = items.length;
    const processed = items.filter((i) => i.extractionStatus !== 'NOT_PROCESSED').length;
    const approved = items.filter((i) => i.extractionStatus === 'APPROVED').length;
    const lowConf = items.filter((i) => i.confidenceBand === 'LOW' && i.extractionStatus === 'COMPLETED').length;
    const failed = items.filter((i) => i.extractionStatus === 'FAILED').length;
    return { total, processed, approved, lowConf, failed };
  }, [items]);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            Document AI Processing
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            OCR + classification + structured field extraction over uploaded documents.
          </p>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat label="Total Docs" value={stats.total} />
        <Stat label="Processed" value={stats.processed} accent="text-primary" />
        <Stat label="Approved" value={stats.approved} accent="text-emerald-600" />
        <Stat label="Low Confidence" value={stats.lowConf} accent="text-amber-600" />
        <Stat label="Failed" value={stats.failed} accent="text-red-600" />
      </div>

      {/* List */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle className="text-sm">Documents</AppCardTitle>
        </AppCardHeader>
        <AppCardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-4"><AppFormSkeleton fields={4} columns={2} /></div>
          ) : items.length === 0 ? (
            <div className="p-6 text-xs text-muted-foreground">
              No documents uploaded for this patient yet. Upload a document to enable AI processing.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-surface-muted border-b">
                  <tr>
                    <th className="w-6 py-2 px-2"></th>
                    <th className="text-left py-2 px-3 font-semibold">File</th>
                    <th className="text-left py-2 px-3 font-semibold">AI Tag</th>
                    <th className="text-left py-2 px-3 font-semibold">Confidence</th>
                    <th className="text-left py-2 px-3 font-semibold">Status</th>
                    <th className="text-left py-2 px-3 font-semibold">Fields</th>
                    <th className="text-left py-2 px-3 font-semibold">Uploaded</th>
                    <th className="py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <DocAiRow
                      key={item.entityDocumentId}
                      item={item}
                      programId={programId}
                      patientId={patientId}
                      isExpanded={expandedDocId === item.entityDocumentId}
                      onToggle={() =>
                        setExpandedDocId(
                          expandedDocId === item.entityDocumentId ? null : item.entityDocumentId,
                        )
                      }
                      onProcessed={(message) => {
                        showToast(message, 'success');
                        qc.invalidateQueries({ queryKey: ['document-ai-list', programId, patientId] });
                      }}
                      onError={(message) => showToast(message, 'error')}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* Footer */}
      <div className="flex items-start gap-2 text-[11px] text-muted-foreground p-3 bg-surface-muted/50 rounded">
        <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0 text-primary" />
        <div>
          <span className="font-semibold">How it works:</span> the engine reads each document, classifies it
          (Invoice / AE Report / Prescription / etc), extracts structured fields, and suggests links to the
          correct case/cycle. All results carry a confidence score and can be edited or overridden before approval.
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="p-2.5 rounded border bg-surface-card">
      <div className="text-[11px] text-muted-foreground uppercase tracking-wide font-semibold">{label}</div>
      <div className={`text-base font-semibold mt-0.5 tabular-nums ${accent ?? ''}`}>{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  Row + expanded detail
// ─────────────────────────────────────────────────────────────────

interface RowProps {
  item: DocAiExtractionListItem;
  programId: string;
  patientId: string;
  isExpanded: boolean;
  onToggle: () => void;
  onProcessed: (message: string) => void;
  onError: (message: string) => void;
}

function DocAiRow({ item, programId, patientId, isExpanded, onToggle, onProcessed, onError }: RowProps) {
  const qc = useQueryClient();

  const processMutation = useMutation({
    mutationFn: (forceReRun: boolean) =>
      documentAiService.process(programId, item.entityDocumentId, forceReRun),
    onSuccess: () => {
      onProcessed(`Extraction completed for ${item.fileName}.`);
      qc.invalidateQueries({ queryKey: ['document-ai-extraction', programId, item.entityDocumentId] });
    },
    onError: (e: any) => onError(e?.response?.data?.message || 'OCR processing failed.'),
  });

  const tagBadge = item.detectedDocumentType ? (
    <Badge className="bg-primary/10 text-primary border-primary/20 text-[11px] border">
      {DOC_AI_TYPE_LABELS[item.detectedDocumentType]}
      {item.tagSource === 'USER_OVERRIDE' && (
        <span className="ml-1 text-[11px] opacity-70">(user)</span>
      )}
    </Badge>
  ) : (
    <span className="text-[11px] text-muted-foreground italic">—</span>
  );

  return (
    <>
      <tr className="border-b hover:bg-surface-muted/50 cursor-pointer" onClick={onToggle}>
        <td className="py-2 px-2">
          {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </td>
        <td className="py-2 px-3">
          <div className="font-medium truncate max-w-[260px]" title={item.fileName}>
            {item.fileName}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {item.contentType ?? 'unknown'}
            {item.fileSizeBytes != null && ` · ${(item.fileSizeBytes / 1024).toFixed(1)} KB`}
          </div>
        </td>
        <td className="py-2 px-3">{tagBadge}</td>
        <td className="py-2 px-3">
          {item.confidenceScore != null ? (
            <ConfidenceBar score={item.confidenceScore} band={item.confidenceBand} />
          ) : (
            <span className="text-[11px] text-muted-foreground">—</span>
          )}
        </td>
        <td className="py-2 px-3">
          <Badge className={`${DOC_AI_STATUS_COLORS[item.extractionStatus]} text-[11px] border-0`}>
            {item.extractionStatus.replace('_', ' ')}
          </Badge>
        </td>
        <td className="py-2 px-3 text-[11px] tabular-nums">
          {item.fieldCount}
          {item.hasSuggestedLinks && <Link2 className="w-3 h-3 inline ml-1 text-primary" />}
        </td>
        <td className="py-2 px-3 text-[11px]">{new Date(item.uploadedAt).toLocaleDateString()}</td>
        <td className="py-2 px-3 text-right">
          <Button
            size="sm"
            variant="outline"
            className="h-6 text-[11px] px-2"
            onClick={(e) => {
              e.stopPropagation();
              processMutation.mutate(item.extractionStatus !== 'NOT_PROCESSED');
            }}
            disabled={processMutation.isPending}
          >
            {processMutation.isPending ? (
              <AppInlineLoader />
            ) : item.extractionStatus === 'NOT_PROCESSED' ? (
              <>
                <FileSearch className="w-3 h-3 mr-1" /> Run OCR
              </>
            ) : (
              <>
                <RefreshCw className="w-3 h-3 mr-1" /> Re-run
              </>
            )}
          </Button>
        </td>
      </tr>

      {isExpanded && (
        <tr className="border-b bg-surface-muted/30">
          <td colSpan={8} className="p-0">
            <DocAiDetailPanel
              programId={programId}
              patientId={patientId}
              entityDocumentId={item.entityDocumentId}
              onChanged={(message) => {
                onProcessed(message);
                qc.invalidateQueries({ queryKey: ['document-ai-list', programId, patientId] });
              }}
              onError={onError}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
//  Detail panel — edit extracted fields, approve, override tag
// ─────────────────────────────────────────────────────────────────

interface DetailProps {
  programId: string;
  patientId: string;
  entityDocumentId: string;
  onChanged: (message: string) => void;
  onError: (message: string) => void;
}

function DocAiDetailPanel({ programId, entityDocumentId, onChanged, onError }: DetailProps) {
  const qc = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [draftType, setDraftType] = useState<DocAiDocumentType | null>(null);
  const [draftFields, setDraftFields] = useState<Record<string, string>>({});

  const detailQuery = useQuery({
    queryKey: ['document-ai-extraction', programId, entityDocumentId],
    queryFn: () => documentAiService.getCurrent(programId, entityDocumentId),
  });

  const extraction = detailQuery.data;

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!extraction) throw new Error('No extraction loaded.');
      const fields: Record<string, unknown> = {};
      Object.entries(draftFields).forEach(([k, v]) => {
        if (v !== undefined) fields[k] = v;
      });
      return documentAiService.update(programId, extraction.documentAiExtractionId, {
        detectedDocumentType: draftType ?? undefined,
        extractedFields: fields,
      });
    },
    onSuccess: () => {
      onChanged('Extraction updated.');
      setEditMode(false);
      setDraftType(null);
      setDraftFields({});
      qc.invalidateQueries({ queryKey: ['document-ai-extraction', programId, entityDocumentId] });
    },
    onError: (e: any) => onError(e?.response?.data?.message || 'Save failed.'),
  });

  const approveMutation = useMutation({
    mutationFn: () => {
      if (!extraction) throw new Error('No extraction loaded.');
      return documentAiService.approve(programId, extraction.documentAiExtractionId, { applyLinks: true });
    },
    onSuccess: () => {
      onChanged('Extraction approved.');
      qc.invalidateQueries({ queryKey: ['document-ai-extraction', programId, entityDocumentId] });
    },
    onError: (e: any) => onError(e?.response?.data?.message || 'Approval failed.'),
  });

  if (detailQuery.isLoading) {
    return <div className="p-4"><AppFormSkeleton fields={4} columns={2} /></div>;
  }
  if (!extraction) {
    return (
      <div className="p-4 text-[11px] text-muted-foreground">
        This document has not been processed yet. Click <strong>Run OCR</strong> on the row to extract data.
      </div>
    );
  }

  const startEdit = () => {
    setEditMode(true);
    setDraftType(extraction.detectedDocumentType);
    const initial: Record<string, string> = {};
    Object.entries(extraction.extractedFields ?? {}).forEach(([k, v]) => {
      initial[k] = v == null ? '' : String(v);
    });
    setDraftFields(initial);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setDraftType(null);
    setDraftFields({});
  };

  const fieldEntries = editMode
    ? Object.entries(draftFields)
    : Object.entries(extraction.extractedFields ?? {}).map(
        ([k, v]) => [k, v == null ? '' : String(v)] as [string, string],
      );

  const isLowConfidence = extraction.confidenceBand === 'LOW' && extraction.extractionStatus === 'COMPLETED';
  const isApproved = extraction.extractionStatus === 'APPROVED';
  const isFailed = extraction.extractionStatus === 'FAILED';

  return (
    <div className="p-4 space-y-3">
      {isFailed && (
        <div className="flex items-start gap-2 p-2.5 rounded bg-red-50 border border-red-200 text-[11px]">
          <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-red-800">OCR processing failed</div>
            <div className="text-red-700">{extraction.errorMessage ?? 'Unknown error.'}</div>
          </div>
        </div>
      )}

      {isLowConfidence && (
        <div className="flex items-start gap-2 p-2.5 rounded bg-amber-50 border border-amber-200 text-[11px]">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-amber-900">
            Low confidence ({Math.round((extraction.confidenceScore ?? 0) * 100)}%) — please review and correct
            extracted data before approval.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Left: extracted fields */}
        <div className="lg:col-span-2 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
              Extracted Fields
            </div>
            <div className="flex gap-1">
              {!editMode && !isApproved && !isFailed && (
                <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={startEdit}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              )}
              {editMode && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 text-[11px] px-2"
                    onClick={cancelEdit}
                    disabled={updateMutation.isPending}
                  >
                    <X className="w-3 h-3 mr-1" /> Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="h-6 text-[11px] px-2"
                    onClick={() => updateMutation.mutate()}
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? (
                      <AppInlineLoader />
                    ) : (
                      <>
                        <Save className="w-3 h-3 mr-1" /> Save
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Document type override */}
          {editMode && (
            <div>
              <Label className="text-[11px]">Document Type</Label>
              <Select
                value={draftType ?? extraction.detectedDocumentType}
                onValueChange={(v) => setDraftType(v as DocAiDocumentType)}
              >
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => (
                    <SelectItem key={t} value={t} className="text-xs">
                      {DOC_AI_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {fieldEntries.length === 0 ? (
            <div className="text-[11px] text-muted-foreground italic p-3 border border-dashed rounded">
              No structured fields extracted.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {fieldEntries.map(([key, value]) => (
                <div key={key}>
                  <Label className="text-[11px] capitalize">
                    {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                  </Label>
                  {editMode ? (
                    <Input
                      className="h-7 text-xs"
                      value={value}
                      onChange={(e) =>
                        setDraftFields((d) => ({ ...d, [key]: e.target.value }))
                      }
                    />
                  ) : (
                    <div className="text-[11px] py-1.5 px-2 rounded bg-surface-muted/50 border min-h-[28px]">
                      {value || <span className="text-muted-foreground italic">empty</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: meta + suggested link + approve */}
        <div className="space-y-2">
          <div className="space-y-1.5 p-2.5 border rounded bg-surface-card">
            <div className="text-[11px] font-semibold text-muted-foreground uppercase">Extraction Meta</div>
            <Meta label="Engine" value={extraction.ocrEngine} />
            <Meta label="Run #" value={String(extraction.runNumber)} />
            <Meta
              label="Processed"
              value={extraction.processedAt ? new Date(extraction.processedAt).toLocaleString() : '—'}
            />
            <Meta
              label="Duration"
              value={extraction.processingDurationMs != null ? `${extraction.processingDurationMs}ms` : '—'}
            />
            <Meta label="Tag source" value={extraction.tagSource} />
            <Meta
              label="Confidence"
              value={
                extraction.confidenceScore != null
                  ? `${Math.round(extraction.confidenceScore * 100)}% (${extraction.confidenceBand})`
                  : '—'
              }
            />
          </div>

          {extraction.suggestedLinks && (
            <div className="space-y-1.5 p-2.5 border rounded bg-primary/5 border-primary/20">
              <div className="text-[11px] font-semibold text-primary uppercase flex items-center gap-1">
                <Link2 className="w-3 h-3" /> Suggested Link
              </div>
              {extraction.suggestedLinks.caseNumber && (
                <Meta label="Case" value={extraction.suggestedLinks.caseNumber} />
              )}
              {extraction.suggestedLinks.cycleNumber != null && (
                <Meta label="Cycle" value={`#${extraction.suggestedLinks.cycleNumber}`} />
              )}
              <Meta
                label="Link score"
                value={`${Math.round(extraction.suggestedLinks.linkScore * 100)}%`}
              />
              {extraction.suggestedLinks.reason && (
                <div className="text-[11px] text-muted-foreground italic mt-1">
                  {extraction.suggestedLinks.reason}
                </div>
              )}
            </div>
          )}

          {!isApproved && !isFailed && extraction.extractionStatus === 'COMPLETED' && (
            <Button
              size="sm"
              className="w-full h-7 text-[11px]"
              onClick={() => approveMutation.mutate()}
              disabled={approveMutation.isPending || editMode}
            >
              {approveMutation.isPending ? (
                <AppInlineLoader className="mr-1" />
              ) : (
                <CheckCircle className="w-3 h-3 mr-1" />
              )}
              Approve Extraction
            </Button>
          )}

          {isApproved && (
            <div className="text-[11px] text-emerald-700 flex items-center gap-1 p-2 bg-emerald-50 border border-emerald-200 rounded">
              <CheckCircle className="w-3 h-3" />
              Approved {extraction.approvedAt && `· ${new Date(extraction.approvedAt).toLocaleDateString()}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2 text-[11px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right truncate" title={value}>
        {value}
      </span>
    </div>
  );
}

function ConfidenceBar({ score, band }: { score: number; band: string }) {
  const pct = Math.round(score * 100);
  const color =
    band === 'HIGH' ? 'bg-emerald-500' : band === 'MEDIUM' ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-14 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] tabular-nums">{pct}%</span>
      <Badge className={`${DOC_AI_BAND_COLORS[band as keyof typeof DOC_AI_BAND_COLORS]} text-[11px] border px-1 py-0`}>
        {band}
      </Badge>
    </div>
  );
}

